import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
    sendOtpRequest,
    sendOtpSuccess,
    sendOtpFailure,
    verifyOtpRequest,
    verifyOtpSuccess,
    verifyOtpFailure,
    resendOtpRequest,
    resendOtpSuccess,
    resendOtpFailure,
    googleLoginRequest,
    appleLoginRequest,
    socialLoginFailure,
} from "../slice/otpAuthSlice";
import { sendOtpApi, verifyOtpApi, resendOtpApi } from "../../api/apiService";
import { iframeMessaging } from "@src/utils/iframeMessaging";

// Send OTP
function* sendOtpSaga(action: PayloadAction<{ email: string }>): Generator<any, void, any> {
    try {
        const { email } = action.payload;
        yield call(sendOtpApi, { email });
        yield put(sendOtpSuccess());
    } catch (error: any) {
        if (error.response && error.response.data && error.response.data.message) {
            yield put(sendOtpFailure(error.response.data.message));
        } else {
            if(error.response && error.response.status === 429) {
                yield put(sendOtpFailure("Too many requests. Please trying again after some time."));
            }
            else{
                yield put(sendOtpFailure("Failed to send OTP"));
            }
        }
    }
}

// Verify OTP
function* verifyOtpSaga(action: PayloadAction<{ email: string; otp: string }>): Generator<any, void, any> {
    try {
        const { email, otp } = action.payload;
        const response = yield call(verifyOtpApi, { email, otp });
        const data = response.data;
        // Store tokens in localStorage
        localStorage.setItem("access_token", data.token.access);
        if (data.token.refresh) {
            localStorage.setItem("refresh_token", data.token.refresh);
        }
        // Send tokens to parent window if needed (real user only)
        iframeMessaging.sendTokenToParentIfNeeded(
            { accessToken: data.token.access, refreshToken: data.token.refresh },
            email,
        );
        yield put(
            verifyOtpSuccess({
                message: data.message,
                token: data.token,
                user_exists: data.user_exists,
            }),
        );
    } catch (error: any) {
        if (error.response && error.response.data && error.response.data.message) {
            yield put(verifyOtpFailure(error.response.data.message));
        } else {
            yield put(verifyOtpFailure("Incorrect OTP. Please try again."));
        }
    }
}

// Resend OTP
function* resendOtpSaga(action: PayloadAction<{ email: string }>): Generator<any, void, any> {
    try {
        const { email } = action.payload;
        yield call(resendOtpApi, { email });
        yield put(resendOtpSuccess());
    } catch (error: any) {
        if (error.response && error.response.data && error.response.data.message) {
            yield put(resendOtpFailure(error.response.data.message));
        } else {
            if(error.response && error.response.status === 429) {
                yield put(resendOtpFailure("Too many requests. Please trying again after some time."));
            }
            else{
                yield put(resendOtpFailure("Failed to resend OTP"));
            }
        }
    }
}

// Google Login (placeholder for future implementation)
function* googleLoginSaga(): Generator<any, void, any> {
    try {
        yield put(socialLoginFailure("Google login is not yet implemented"));
    } catch (error: any) {
        yield put(socialLoginFailure(error.message || "Google login failed"));
    }
}

// Apple Login (placeholder for future implementation)
function* appleLoginSaga(): Generator<any, void, any> {
    try {
        yield put(socialLoginFailure("Apple login is not yet implemented"));
    } catch (error: any) {
        yield put(socialLoginFailure(error.message || "Apple login failed"));
    }
}

export function* otpAuthSaga() {
    yield takeLatest(sendOtpRequest.type, sendOtpSaga);
    yield takeLatest(verifyOtpRequest.type, verifyOtpSaga);
    yield takeLatest(resendOtpRequest.type, resendOtpSaga);
    yield takeLatest(googleLoginRequest.type, googleLoginSaga);
    yield takeLatest(appleLoginRequest.type, appleLoginSaga);
}
export { sendOtpSaga, verifyOtpSaga, resendOtpSaga, googleLoginSaga, appleLoginSaga };